import GameGlobals, { GameMode } from "../../../../../components/Game/GameGlobals"
import TooltipCheckbox from "../../../../../components/tooltip/TooltipCheckbox"
import { nodePalette } from "../../../../../theme"
import progressCircleGraphics from "../../../../graphics/ProgressCircleGraphics"
import ManaSparkParticle from "../../../../physics/magic/ManaSparkParticle"
import SimpleEntity from "../../../../physics/SimpleEntity"
import ManaParticle from "../../../ManaParticle"
import NodeCategories from "../../NodeCategories"
import SimpleManaNode, { SimpleManaNodeOptions } from "../../SimpleManaNode"

export type ManaSourceOptions = {
	mana?: number
	maxMana?: number
	regen?: number
	particleCooldown?: number
	caster?: SimpleEntity
	infinite?: boolean
} & SimpleManaNodeOptions

export default class ManaSource extends SimpleManaNode {

	mana
	maxMana
	regen
	particleCooldown
	infinite

	private regenInterval
	private spawnParticleInterval

	static nodeName = "Mana Source"
	static nodeDesc = "Generates and stores mana."
	static nodeIcon = '🌀'
	static category = NodeCategories.Unique
	static nodeType = 'ManaSource'

	static baseSize = 40 * 3

	constructor({ infinite = false, mana = 100, maxMana = 100, regen = 1, particleCooldown = 50, ...rest }: ManaSourceOptions) {
		super({
			...rest,

			size: ManaSource.baseSize,
		})

		this.mana = mana
		this.maxMana = maxMana
		this.regen = regen
		this.particleCooldown = particleCooldown
		this.infinite = infinite

		this.symbol = this.getSymbol()

		this.regenInterval = setInterval(() => {
			if (this.world?.caster.isDead) {
				this.mana *= 0.75
			} else {
				this.mana = Math.min(this.maxMana, this.mana + this.regen)
			}
		}, 1000)

		this.spawnParticleInterval = setInterval(() => {

			this.spawnParticle()

		}, particleCooldown)
	}

	getTooltip() {
		if (GameGlobals.gameMode === GameMode.SANDBOX) {
			return (
				<>
					{super.getTooltip()}
					<br />
					<TooltipCheckbox
						title={
							<>
								<span style={{ fontFamily: "Segoe UI Emoji", marginRight: 8 }}>🛠</span>
								<span>Infinite source</span>
							</>
						}
						initialChecked={this.infinite}
						onChange={infinite => this.setInfinite(infinite)}
					/>
				</>
			)
		} else {
			return super.getTooltip()
		}
	}

	// disable sizing
	onScroll(dir: number) { }

	setInfinite(infinite: boolean) {
		this.infinite = infinite
		this.mana = this.maxMana
		this.reloadSymbol()
	}

	getSymbol() {
		return this.infinite ? '♾' : ManaSource.nodeIcon
	}

	spawnParticle() {
		const caster = this.getCaster()

		if (caster && (!caster.isDead)) {
			const manaSpark = new ManaSparkParticle({
				world: caster.world,

				x: caster.position.x,
				y: caster.position.y,
			})

			caster.world.add(manaSpark)
			const particle = new ManaParticle({
				source: this,
				caster: caster,
				target: manaSpark
			})

			this.addParticle(particle)
		}
	}

	getCaster() {
		return this.world?.caster
	}

	getBaseSize() {
		return ManaSource.baseSize
	}

	consumeMana(amt: number) {
		if (!this.infinite) {
			this.mana -= amt
		}
		// integrate Mana consumption to XP here
	}

	dispose() {
		super.dispose()

		clearInterval(this.regenInterval)
		clearInterval(this.spawnParticleInterval)
	}

	createGraphics() {
		return progressCircleGraphics({
			obj: this,
			x: this.position.x,
			y: this.position.y,
			size: this.size,
			symbol: this.symbol,
			color: this.color,
			progress: () => (this.mana / this.maxMana),
			border: nodePalette.active,
			arcColor: nodePalette.active,
			evented: true
		})
	}

	save() {
		return {
			...super.save(),

			infinite: this.infinite,
			mana: this.mana,
			maxMana: this.maxMana,
			regen: this.regen,
			particleCooldown: this.particleCooldown,
		}
	}

}